import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Device } from '../types';
import { buildOtaMqttPreview } from '../utils/firmwareUrl';

interface FirmwareFile {
  name: string;
  path: string;
  uploadedAt?: string;
}

interface FirmwareUpdateModalProps {
  apiBaseUrl: string;
  device: Device | null;
  isOpen: boolean;
  onClose: () => void;
}

const EXCLUDED_BIN_PATTERNS = [/bootloader/i, /partition/i, /boot_app/i];

function isFirmwareBin(file: FirmwareFile) {
  const value = `${file.name} ${file.path}`;
  return file.name.endsWith('.bin') && !EXCLUDED_BIN_PATTERNS.some((pattern) => pattern.test(value));
}

const FirmwareUpdateModal: React.FC<FirmwareUpdateModalProps> = ({
  apiBaseUrl,
  device,
  isOpen,
  onClose,
}) => {
  const [firmwareFiles, setFirmwareFiles] = useState<FirmwareFile[]>([]);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const fetchFirmwareFiles = async () => {
      setLoading(true);
      setError(null);
      setSelectedPath('');

      try {
        const response = await axios.get(`${apiBaseUrl}/firmware/list`, {
          withCredentials: true,
        });
        const files = (response.data.files || []) as FirmwareFile[];
        const sortedFiles = files
          .filter(isFirmwareBin)
          .sort((a, b) => b.name.localeCompare(a.name));

        setFirmwareFiles(sortedFiles);
        if (sortedFiles.length > 0) {
          setSelectedPath(sortedFiles[0].path);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load firmware bins');
      } finally {
        setLoading(false);
      }
    };

    fetchFirmwareFiles();
  }, [apiBaseUrl, isOpen]);

  const selectedFile = useMemo(
    () => firmwareFiles.find((file) => file.path === selectedPath),
    [firmwareFiles, selectedPath]
  );

  const otaCommand = selectedPath ? buildOtaMqttPreview(selectedPath) : '';

  const handleSubmit = async () => {
    if (!device || !selectedPath) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post(
        `${apiBaseUrl}/devices/${device.serial}/firmware-update`,
        { firmwarePath: selectedPath },
        { withCredentials: true }
      );
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send firmware update command');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !device) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Install Firmware</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                This will immediately send an OTA command to <strong>{device.serial}</strong>.
              </div>

              <div className="mb-3">
                <div className="small text-muted mb-1">Device</div>
                <div>
                  <strong>{device.model}</strong> running {device.firmware}
                </div>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading firmware bins...</span>
                  </div>
                </div>
              ) : (
                <>
                  <label className="form-label" htmlFor="firmware-bin-select">
                    Firmware bin
                  </label>
                  <select
                    id="firmware-bin-select"
                    className="form-select"
                    value={selectedPath}
                    onChange={(event) => setSelectedPath(event.target.value)}
                    disabled={firmwareFiles.length === 0}
                  >
                    {firmwareFiles.length === 0 ? (
                      <option value="">No firmware bins available</option>
                    ) : (
                      firmwareFiles.map((file) => (
                        <option key={file.path} value={file.path}>
                          {file.name}
                        </option>
                      ))
                    )}
                  </select>

                  {selectedFile && (
                    <div className="mt-3">
                      <div className="small text-muted">MQTT command</div>
                      <code className="d-block text-break">{otaCommand}</code>
                      {selectedFile.uploadedAt && (
                        <div className="small text-muted mt-1">
                          Uploaded {new Date(selectedFile.uploadedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!selectedPath || loading || submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FirmwareUpdateModal;
